
import { deep_clone } from "@/global.functions";
import {
  TileConfig,
  TileIndex,
  TileNames,
  UpdateTileInterface,
} from "@/store/config.types";
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import SpacecraftMapConfigComponent from "./spacecraftmap-config.vue";
import StreamConfigComponent from "./stream-config.vue";
import LiveEventsConfigComponent from "./liveevents-config.vue";
import TelemetryConfigComponent from "./telemetry-config.vue";
import ISSMCCMapConfigComponent from "./issmccmap-config.vue";
import { DEFAULT_TILE_CONFIG } from "../../store/default-configs";
import HelpComponent from "../help.vue";

const HELP_TEXTS = {
  "": "No tile type selected yet. This means there's only an empty space visible.\n\n### Available tile types\nThe following tile types are available\n* **Stream**: Shows the selected YouTube stream\n* **Telemetry**: Shows the selected telemetry datapoints of the public NASA ISS telemetry.\n* **Spacecraft Map**: Shows a map with the selected spacecraft.\n* **ISS MCC Map**: Shows a map with features close to the one in the ISS Mission Control (ISS-MCC) located in Houston, TX in the Lyndon B. Johnson Space Center. It tries to replicate their software \"Demos 2D World Map\".",
  "stream": "The stream tile displays a YouTube stream by NASA you select.\n\n### Stream Config\nHere you select the **stream**, allow it to **autoplay** on load, **Enable Controls** shows the YouTube Player control surfaces and you can **mute** the audio of a stream",
  "liveevents": "The live event stream tile displays the next or currently running NASA+ live event",
  "telemetry": "The telemetry tile displays the current public ISS telemetry data.\n\n### Telemetry Config\nHere you can chose to highlight the changes (**Enable highlighting**) and you can choose which datapoints should be visible and in which order. Hovering over an added datapoint displays a more detailed description.",
  "spacecraftmap": "The Spacecraft Map tile displays a worldmap with day/night and the location and (current and two future) orbits of a spacecraft you select.\n\n### Map Config\nYou can choose up to five **spacecraft** to be displayed. Additionally you can select a spacecraft to be followed (**Follow Spacecraft**) and enable the follow-mode. (Enabling the follow mode is also possible from the sidebar in the tile, as well as showing popups.)",
  "issmccmap": "The ISS MCC Map tile displays a map with features close to the one in the ISS Mission Control (ISS-MCC) located in Houston, TX in the Lyndon B. Johnson Space Center. It tries to replicate their software \"Demos 2D World Map\". The ISS location and its (current and two future) orbits are always shown, but you may select optional spacecraft to be shown.\n\n### ISS MCC Map Config\nYou can select the optional **spacecraft** to be displayed alongside the ISS. You're also able to select a spacecraft to be followed (**Follow Spacecraft**) and enable the follow-mode. (Enabling the follow mode is also possible from the sidebar in the tile, as well as showing popups.)\n\n### Features in the map\nAs this map is fairly complex here are some explainations:\n* white sphere around ISS: This is the footprint of the ISS, meaning if your on the ground in this location it is above the horizon.\n* SUN: Marks the current position of the sun.\n* T041W, T171W, T275W: Marks the current positions of the TDRS 7 (T275W), TDRS 10 (T171W) and TDRS 12 (T041W). The polygons in the color of the satellite mark the locations where this satellite isn't in range for the ISS.\n* ZOE (Zone Of Exclusion): Under normal operation only TRDS 10 (T170W) and TDRS 12 (T041W) are used for communicating with the ISS. In the overlap of the no-signal polygons of TDRS 10 and 12 the ISS would be in a LOS (Loss Of Signal). In emergency or other exceptional situations where signal is needed in this location also TDRS 7 (T275W) is used. While in normal operation the saves costs and makes more bandwith available to other customers.\n* SAA (South Atlantic Anomaly Zone): This polygon represents an area in which the radiation protection of the earths magnetic field is restricted. It's helpful for Mission Control to know this as e.g electronics are more likely to fail while the ISS is passing through this zone.\n* STPB, DJS, ULD, USK, PPK: Russian ground stations. While the ISS or other spacecraft are above this groundstations Roscosmos has the capability to communicate directly with them. (Groundstations: STPB = Saint Petersburg?, DJS = Baikonour, ULD = Ulan Ude, USK = Kirovskiy?, PPK = Petropavlovsk-Kamchatsky)"
}

const TILE_TYPES = {
  "stream": "Stream",
  "liveevents": "Live Event Stream",
  "telemetry": "Telemetry",
  "spacecraftmap": "Spacecraft Map",
  "issmccmap": "ISS MCC Map",
};

@Component({
  components: {
    "stream-config": StreamConfigComponent,
    "liveevents-config": LiveEventsConfigComponent,
    "telemetry-config": TelemetryConfigComponent,
    "spacecraftmap-config": SpacecraftMapConfigComponent,
    "issmccmap-config": ISSMCCMapConfigComponent,
    "help": HelpComponent
  },
})
export default class BodyElementConfigComponent extends Vue {
  show: boolean = false;
  @Prop() tileIndex!: TileIndex;
  tileConfig: TileConfig = deep_clone(
      this.$store.getters["config/getTileConfig"](this.tileIndex)
    ) as TileConfig;
  restoreConfirmationDialog: boolean = false

  get tileConfigName() {
    return this.tileConfig.name;
  }

  set tileConfigName(value: TileNames) {
    this.tileConfig.name = value;
    if (!this.tileConfig.name) this.tileConfig.name = "";
    Vue.set(this.tileConfig, "config", deep_clone(DEFAULT_TILE_CONFIG[this.tileConfigName]))
  }

  get tileTypes() {
    return Object.entries(TILE_TYPES).map((entry) => ({
      text: entry[1],
      value: entry[0],
    }));
  }

  get contextHelp() {
    return `## Configure Tile\n${HELP_TEXTS[this.tileConfigName]}`
  }

  restoreDefaultConfig() {
    Vue.set(this.tileConfig, "config", deep_clone(DEFAULT_TILE_CONFIG[this.tileConfigName]))
    this.restoreConfirmationDialog = false
  }

  save() {
    this.$store.dispatch("config/updateTile", {
      index: this.tileIndex,
      tileConfig: this.tileConfig,
    } as UpdateTileInterface);
    this.show = false;
  }

  cancel() {
    this.tileConfig = deep_clone(
      this.$store.getters["config/getTileConfig"](this.tileIndex)
    ) as TileConfig;
    this.show = false;
  }

  @Watch("show")
  watchShow(newValue: boolean) {
    this.$emit("update:tileEdit", newValue);
  }
}
